// FILE: src/access/pageAccess.js
// Canonical page permission definitions for Account Managers.
//
// Account Managers are ALWAYS read-only. When access is enabled (grant.active === true),
// they only see the default menu items unless extra pages are explicitly granted.
//
// IMPORTANT: Home is BLOCKED by default and can be optionally unlocked via page access.

// ---- Default (always-on) manager pages ----
export const DEFAULT_MANAGER_MENU_KEYS = [
  "advance_healthcare_directive",
  "organ_donation",
  "funeral_planning",
  "contacts",
  "important_documents",
  "support_ticket",
];

export const DEFAULT_MANAGER_PAGE_ACCESS = DEFAULT_MANAGER_MENU_KEYS.reduce(
  (acc, k) => {
    acc[k] = true;
    return acc;
  },
  {}
);

export const isDefaultManagerMenuKey = (key) =>
  DEFAULT_MANAGER_MENU_KEYS.includes(key);

// ---- All pages (used by UI + sidebar filtering) ----
// NOTE: key values must match ManagerPageGate pageKey + Firestore rules pageKey.
export const PAGE_ACCESS = [
  {
    key: "home",
    label: "Home",
    description: "Dashboard overview. Blocked by default.",
    path: "/",
    group: "Core",
  },
  {
    key: "contacts",
    label: "Contacts",
    description: "Emergency and key contacts list.",
    path: "/contacts",
    group: "Core",
  },

  // Tenant owner/admin pages
  {
    key: "invite_managers",
    label: "Invite Managers",
    description: "Invite and manage account manager access.",
    path: "/account-managers/invite",
    group: "Account Managers",
  },
  {
    key: "managed_accounts_admin",
    label: "Managed Accounts",
    description: "View and manage linked managed accounts.",
    path: "/account-managers/managed",
    group: "Account Managers",
  },

  {
    key: "advance_healthcare_directive",
    label: "Advance Healthcare Directive",
    description: "Read the saved healthcare directive.",
    path: "/legal/advance-healthcare-directive",
    group: "Legal",
  },
  {
    key: "organ_donation",
    label: "Organ Donation",
    description: "View organ donation preferences.",
    path: "/personal/organ-donation",
    group: "Personal",
  },
  {
    key: "funeral_planning",
    label: "Funeral Planning",
    description: "View funeral wishes and arrangements.",
    path: "/personal/funeral-planning",
    group: "Personal",
  },
  {
    key: "important_documents",
    label: "Important Documents",
    description: "View uploaded important documents.",
    path: "/legal/important-documents",
    group: "Legal",
  },
  {
    key: "support_ticket",
    label: "Support Ticket",
    description: "Contact support and view tickets.",
    path: "/support",
    group: "Support",
  },

  // ---- Optional extras (not granted by default) ----
  {
    key: "secure_e_will",
    label: "Secure E-Will",
    description: "View or access the saved E-Will.",
    path: "/legal/secure-e-will",
    group: "Legal",
  },
  {
    key: "pensions",
    label: "Pensions",
    description: "View pension details.",
    path: "/legal/pensions",
    group: "Legal",
  },
  {
    key: "password_vault",
    label: "Password Vault",
    description: "View stored passwords (read-only).",
    path: "/vaults/password-vault",
    group: "Vaults",
  },
  {
    key: "digital_platforms",
    label: "Digital Platforms",
    description: "View online accounts and platforms.",
    path: "/vaults/digital-platforms",
    group: "Vaults",
  },
  {
    key: "devices",
    label: "Devices",
    description: "View stored devices.",
    path: "/vaults/devices",
    group: "Vaults",
  },
  {
    key: "personal_messages",
    label: "Personal Messages",
    description: "View personal messages.",
    path: "/messages/personal-messages",
    group: "Messages",
  },
  {
    key: "time_vault",
    label: "Time Vault",
    description: "View time-locked messages (available after the release date).",
    path: "/messages/time-vault",
    group: "Messages",
  },
  {
    key: "last_goodbyes",
    label: "Last Goodbyes",
    description: "View last goodbye messages.",
    path: "/messages/last-goodbyes",
    group: "Messages",
  },
  {
    key: "videos",
    label: "Videos",
    description: "View saved videos.",
    path: "/messages/videos",
    group: "Messages",
  },
  {
    key: "legacy_message",
    label: "Legacy Message",
    description: "View the legacy message.",
    path: "/messages/legacy-message",
    group: "Messages",
  },
  {
    key: "proof_of_life",
    label: "Proof of Life",
    description: "View proof-of-life records.",
    path: "/proof-of-life",
    group: "Support",
  },
  {
    key: "security",
    label: "Security",
    description: "View security/MFA settings (read-only).",
    path: "/security",
    group: "Support",
  },
  {
    key: "memory_lane",
    label: "Memory Lane",
    description: "View memory lane content.",
    path: "/personal/memory-lane",
    group: "Personal",
  },
  {
    key: "belongings",
    label: "Belongings",
    description: "View belongings list.",
    path: "/personal/belongings",
    group: "Personal",
  },
];

export const PAGE_KEY_SET = new Set(PAGE_ACCESS.map((p) => p.key));

export function buildNoneAccessMap() {
  const m = {};
  for (const p of PAGE_ACCESS) m[p.key] = false;
  return m;
}

export function buildFullAccessMap() {
  const m = {};
  for (const p of PAGE_ACCESS) m[p.key] = true;
  return m;
}

export function normalizePageAccess(input) {
  const src = input && typeof input === "object" ? input : {};
  const out = {};
  for (const k of PAGE_KEY_SET) out[k] = src[k] === true;
  return out;
}

// Ensures defaults are always ON, even if pageAccess is missing
export function applyManagerDefaults(pageAccess = {}) {
  const merged = { ...normalizePageAccess(pageAccess) };
  for (const k of DEFAULT_MANAGER_MENU_KEYS) merged[k] = true;
  return merged;
}

// Canonical way to decide what a manager can see.
// We treat missing/unknown accessMode as LIMITED.
export function getEffectiveManagerAccess(grant) {
  if (!grant) {
    return {
      accessMode: "limited",
      pageAccess: applyManagerDefaults({}),
      active: false,
    };
  }

  const accessMode =
    String(grant.accessMode || "limited").toLowerCase() === "full"
      ? "full"
      : "limited";
  const pageAccess = applyManagerDefaults(grant.pageAccess || {});
  const active = grant.active === true;

  // IMPORTANT: we still return accessMode for backward compatibility,
  // but the UI + rules should rely on pageAccess (defaults + extras).
  return { accessMode, pageAccess, active };
}

export function canManagerSeePage(grant, pageKey) {
  const eff = getEffectiveManagerAccess(grant);
  if (!eff.active) return false;
  // We intentionally do NOT default to full access.
  return eff.pageAccess?.[pageKey] === true;
}

// Convenience: all extra pages (not in defaults). Includes Home.
export const EXTRA_MANAGER_PAGES = PAGE_ACCESS.filter(
  (p) => !isDefaultManagerMenuKey(p.key)
);

// ---- Tenant navigation visibility (all ON by default) ----
export const TENANT_STANDALONE_ACCESS = [
  { key: "home", label: "Home" },
  { key: "contacts", label: "Contacts" },
];

export const TENANT_GROUP_ACCESS = [
  {
    key: "group_account_managers",
    label: "Account Managers",
    children: ["invite_managers", "managed_accounts_admin"],
  },
  {
    key: "group_vaults",
    label: "Vaults",
    children: ["password_vault", "digital_platforms", "devices"],
  },
  {
    key: "group_messages",
    label: "Messages",
    children: ["personal_messages", "time_vault", "last_goodbyes", "videos", "legacy_message"],
  },
  {
    key: "group_legal",
    label: "Legal",
    children: ["important_documents", "advance_healthcare_directive", "secure_e_will", "pensions"],
  },
  {
    key: "group_personal",
    label: "Personal",
    children: ["organ_donation", "funeral_planning", "memory_lane", "belongings"],
  },
  {
    key: "group_support",
    label: "Support",
    children: ["proof_of_life", "security", "support_ticket"],
  },
];

export const TENANT_ACCESS_KEYS = [
  ...TENANT_STANDALONE_ACCESS.map((p) => p.key),
  ...TENANT_GROUP_ACCESS.map((g) => g.key),
  ...TENANT_GROUP_ACCESS.flatMap((g) => g.children),
];

const TENANT_ACCESS_KEY_SET = new Set(TENANT_ACCESS_KEYS);

export function buildFullTenantAccessMap() {
  const out = {};
  for (const k of TENANT_ACCESS_KEYS) out[k] = true;
  return out;
}

export function applyTenantAccessDefaults(input = {}) {
  const src = input && typeof input === "object" ? input : {};
  const out = buildFullTenantAccessMap();
  for (const k of TENANT_ACCESS_KEY_SET) {
    if (src[k] === false) out[k] = false;
    if (src[k] === true) out[k] = true;
  }
  return out;
}

export function getPageAccessLabel(key) {
  return PAGE_ACCESS.find((p) => p.key === key)?.label || key;
}
